"use client";

import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Clock,
  MapPin,
  MessageSquare,
  BookOpen,
  Calendar,
  ArrowRight,
  ArrowLeft,
  Loader2,
  CheckCircle2,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface SettingsStepProps {
  onNext: () => void;
  onBack?: () => void;
  isFirst?: boolean;
  isLast?: boolean;
}

interface FeatureToggle {
  id: string;
  key: keyof OrganizationSettings;
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  enabled: boolean;
}

interface OrganizationSettings {
  timeTrackingEnabled: boolean;
  gpsTrackingEnabled: boolean;
  smsEnabled: boolean;
  priceBookEnabled: boolean;
  bookingEnabled: boolean;
}

export function SettingsStep({ onNext, onBack }: SettingsStepProps) {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const [features, setFeatures] = useState<FeatureToggle[]>([
    {
      id: "timeTracking",
      key: "timeTrackingEnabled",
      title: "Time Tracking",
      description: "Track work hours, breaks, and labor costs for accurate job costing",
      icon: Clock,
      enabled: true,
    },
    {
      id: "gpsTracking",
      key: "gpsTrackingEnabled",
      title: "GPS Tracking",
      description: "Real-time location tracking and geofencing for your team",
      icon: MapPin,
      enabled: true,
    },
    {
      id: "sms",
      key: "smsEnabled",
      title: "Two-Way SMS",
      description: "Send and receive text messages with clients directly",
      icon: MessageSquare,
      enabled: true,
    },
    {
      id: "priceBook",
      key: "priceBookEnabled",
      title: "Price Book",
      description: "Maintain standardized pricing for services and inventory items",
      icon: BookOpen,
      enabled: true,
    },
    {
      id: "booking",
      key: "bookingEnabled",
      title: "Online Booking",
      description: "Allow clients to book appointments through your client portal",
      icon: Calendar,
      enabled: true,
    },
  ]);

  // Fetch current organization settings
  useEffect(() => {
    async function fetchSettings() {
      try {
        const response = await fetch("/api/organization");
        if (!response.ok) throw new Error("Failed to fetch settings");

        const organization = await response.json();

        // Update features with current settings
        setFeatures((prev) =>
          prev.map((feature) => ({
            ...feature,
            enabled: organization[feature.key] ?? true,
          }))
        );
      } catch (error) {
        console.error("Error fetching organization settings:", error);
        toast({
          title: "Error",
          description: "Failed to load settings. Using defaults.",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    }

    fetchSettings();
  }, [toast]);

  const handleToggle = (featureId: string) => {
    setFeatures((prev) =>
      prev.map((feature) =>
        feature.id === featureId
          ? { ...feature, enabled: !feature.enabled }
          : feature
      )
    );
    setHasChanges(true);
  };

  const saveSettings = async () => {
    setIsSaving(true);
    try {
      const settings: Partial<OrganizationSettings> = {};
      features.forEach((feature) => {
        settings[feature.key] = feature.enabled;
      });

      const response = await fetch("/api/organization", {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(settings),
      });

      if (!response.ok) {
        throw new Error("Failed to save settings");
      }

      toast({
        title: "Settings saved",
        description: "Your feature preferences have been updated.",
      });
      setHasChanges(false);
      return true;
    } catch (error) {
      console.error("Error saving settings:", error);
      toast({
        title: "Error",
        description: "Failed to save settings. Please try again.",
        variant: "destructive",
      });
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const handleNext = async () => {
    if (hasChanges) {
      const saved = await saveSettings();
      if (saved) {
        onNext();
      }
    } else {
      onNext();
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4 py-4 max-w-4xl mx-auto">
      <div className="text-center space-y-1">
        <h2 className="text-2xl font-bold tracking-tight">
          Customize Your Experience
        </h2>
        <p className="text-muted-foreground text-sm">
          Enable the features that work best for your business
        </p>
      </div>

      <div className="bg-info/10 dark:bg-blue-950 rounded-lg p-3 text-xs text-center">
        You can always change these settings later from your Settings page
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {features.map((feature) => (
          <Card key={feature.id} className="p-4">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-info/10 dark:bg-blue-900">
                  <feature.icon className="h-5 w-5 text-info dark:text-info" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <h3 className="text-sm font-semibold truncate">{feature.title}</h3>
                    {feature.enabled && (
                      <CheckCircle2 className="h-3.5 w-3.5 text-success shrink-0" />
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground line-clamp-2">
                    {feature.description}
                  </p>
                </div>
              </div>
              <Switch
                id={feature.id}
                checked={feature.enabled}
                onCheckedChange={() => handleToggle(feature.id)}
                disabled={isSaving}
              />
            </div>
          </Card>
        ))}
      </div>

      {hasChanges && (
        <div className="bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded-lg p-4 text-sm text-center">
          You have unsaved changes. They will be saved when you continue.
        </div>
      )}

      <div className="flex justify-between pt-4">
        <Button
          variant="outline"
          onClick={onBack}
          className="gap-2"
          disabled={isSaving}
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </Button>
        <Button
          onClick={handleNext}
          className="gap-2"
          disabled={isSaving}
        >
          {isSaving ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              Continue
              <ArrowRight className="h-4 w-4" />
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
